import { ReactNode, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Users, TableProperties, BarChart3, Trophy, Loader2, Sun, Moon, Share2, MoreHorizontal } from "lucide-react";
import { PublicChampionshipProvider, usePublicChampionship } from "@/contexts/PublicChampionshipContext";
import { ThemeProvider, useTheme } from "@/hooks/useTheme";
import { PublicApoiadores } from "@/components/public/PublicBanner";
import { toast } from "sonner";
import { isSinglesMode } from "@/lib/singlesMode";
import { translateSportModalityCombo } from "@/lib/dbTranslate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import logo from "@/assets/logo.png";

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const buildTabs = (t: TFn, teamLabel: string, singlesMode: boolean, hasGroups: boolean) => {
  const showTeamsTab = !singlesMode || hasGroups;
  return [
    { label: t("publicLayout.tabs.overview"), path: "", icon: LayoutDashboard },
    ...(showTeamsTab ? [{ label: singlesMode && hasGroups ? t("publicLayout.tabs.groups") : teamLabel, path: "/teams", icon: Users }] : []),
    { label: t("publicLayout.tabs.stats"), path: "/stats", icon: BarChart3 },
    { label: t("publicLayout.tabs.schedule"), path: "/schedule", icon: TableProperties },
    { label: t("publicLayout.tabs.social"), path: "/social", icon: Share2 },
  ];
};


function InnerLayout({ children }: {children: ReactNode;}) {
  const { t, i18n } = useTranslation("pages");
  const { championship, isLoading, slug } = usePublicChampionship();
  const navigate = useNavigate();
  const location = useLocation();
  const singlesMode = isSinglesMode(championship);
  const hasGroups = championship?.format === "grupo_mata_mata";
  const isTennisDuplas = championship?.sport?.toLowerCase().includes("tênis") && championship?.game_mode === "Duplas";
  const teamLabel = isTennisDuplas ? t("publicLayout.tabs.doubles") : t("publicLayout.tabs.teams");
  const tabs = useMemo(() => buildTabs(t, teamLabel, singlesMode, hasGroups), [t, teamLabel, singlesMode, hasGroups]);
  const sportModalityDisplay = championship
    ? [
        translateSportModalityCombo(championship.sport, championship.modality, i18n.language),
        (championship as any).game_mode,
      ].filter(Boolean).join(" – ")
    : "";

  // Update document head with OG meta tags for this championship
  useEffect(() => {
    if (!championship) return;
    const ogTags: Record<string, string> = {
      "og:title": championship.name,
      "og:description": [championship.sport, championship.modality, championship.game_mode].filter(Boolean).join(" – "),
      "og:image": championship.image_url || "",
      "og:url": window.location.href,
    };
    const cleanups: (() => void)[] = [];
    Object.entries(ogTags).forEach(([property, content]) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (el) {
        const orig = el.getAttribute("content") || "";
        el.setAttribute("content", content);
        cleanups.push(() => el!.setAttribute("content", orig));
      } else {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        el.setAttribute("content", content);
        document.head.appendChild(el);
        cleanups.push(() => el!.remove());
      }
    });
    const origTitle = document.title;
    document.title = `${championship.name} - Sport Control`;
    return () => {
      document.title = origTitle;
      cleanups.forEach(fn => fn());
    };
  }, [championship]);

  const basePath = `/c/${slug}`;

  // Determine active tab by matching path segments
  const pathAfterBase = location.pathname.startsWith(basePath) ?
  location.pathname.slice(basePath.length) :
  "";

  // Get the first segment after the base path
  const firstSegment = pathAfterBase.split("/").filter(Boolean)[0] || "";

  // Map to tab path
  const effectiveTab = firstSegment ? `/${firstSegment}` : "";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>);

  }

  if (!championship) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center text-center p-6">
        <Trophy className="w-10 h-10 text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground text-sm">{t("publicLayout.notFound")}</p>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      {/* Site logo - top left */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <a href="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src={logo} alt="Sport Control" className="w-8 h-8" />
          <span className="text-base font-bold text-foreground tracking-tight whitespace-nowrap" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}>
            Sport Control
          </span>
        </a>
      </div>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-5">
        {/* Header - Logo + Name centered */}
        <div className="flex flex-col items-center gap-2 pt-2">
          {championship.image_url ?
          <img src={championship.image_url} alt="" className="w-28 h-28 rounded-full object-cover" /> :

          <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center">
              <Trophy className="w-12 h-12 text-muted-foreground/50" />
            </div>
          }
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">
              {championship.name}
            </h1>
            {championship.sport &&
            <p className="text-xs text-muted-foreground mt-1 sm:hidden">
                {sportModalityDisplay}
              </p>
            }
          </div>
        </div>

        {/* Tabs + Sport info on the right */}
        <ResponsiveTabs
          tabs={tabs}
          basePath={basePath}
          effectiveTab={effectiveTab}
          firstSegment={firstSegment}
          onNavigate={(p) => navigate(`${basePath}${p}`)}
          rightSlot={
            championship.sport ? (
              <span className="text-xs text-muted-foreground whitespace-nowrap pl-3 hidden sm:block shrink-0">
                {sportModalityDisplay}
              </span>
            ) : null
          }
        />

        {/* Status badge (only on overview) + Theme toggle */}
        <div className="flex items-center justify-between">
          {effectiveTab === "" ? (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              championship.status === "finalizado"
                ? "bg-destructive/10 text-destructive"
                : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            }`}>
              {championship.status === "finalizado" ? t("publicLayout.statusFinished") : t("publicLayout.statusOngoing")}
            </span>
          ) : <div />}
          <div className="flex items-center gap-1">
            <ShareButton slug={slug} />
            <LanguageSwitcher />
            <ThemeToggleButton />
          </div>
        </div>

        {/* Content */}
        {children}

        {/* Apoiadores footer */}
        <PublicApoiadores />
      </div>
    </div>);

}

function ShareButton({ slug }: { slug: string }) {
  const { t } = useTranslation("pages");
  const handleShare = async () => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const shareUrl = `${supabaseUrl}/functions/v1/og?s=${encodeURIComponent(slug)}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("publicLayout.linkCopied"));
    } catch {
      toast.error(t("publicLayout.linkCopyError"));
    }
  };
  return (
    <button
      onClick={handleShare}
      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      aria-label={t("publicLayout.share")}>
      <Share2 className="w-4 h-4" />
    </button>
  );
}

function ThemeToggleButton() {
  const { t } = useTranslation("pages");
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      aria-label={theme === "dark" ? t("publicLayout.lightMode") : t("publicLayout.darkMode")}>
      
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>);

}

interface TabItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

function ResponsiveTabs({
  tabs,
  basePath,
  effectiveTab,
  firstSegment,
  onNavigate,
  rightSlot,
}: {
  tabs: TabItem[];
  basePath: string;
  effectiveTab: string;
  firstSegment: string;
  onNavigate: (path: string) => void;
  rightSlot?: ReactNode;
}) {
  const { t } = useTranslation("pages");
  const navRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const isTabActive = useCallback((tabPath: string) => {
    if (tabPath === "/teams" && (effectiveTab === "/teams" || effectiveTab === "/players")) return true;
    if (tabPath === "" && effectiveTab === "" && !["matches"].includes(firstSegment)) return true;
    if (tabPath === "" && effectiveTab === "") return true;
    if (tabPath !== "" && tabPath !== "/teams" && effectiveTab === tabPath) return true;
    if (tabPath === "/schedule" && effectiveTab === "/matches") return true;
    return false;
  }, [effectiveTab, firstSegment]);

  const measureTabs = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const children = Array.from(nav.querySelectorAll<HTMLElement>("[data-tab]"));
    if (children.length === 0) return;

    const availableWidth = nav.offsetWidth;
    if (availableWidth === 0) return;

    const moreButtonWidth = 64;
    let usedWidth = 0;
    let count = 0;

    for (const child of children) {
      const prev = child.style.cssText;
      child.style.cssText = "position:absolute;visibility:hidden;display:flex;white-space:nowrap;";
      const w = child.offsetWidth;
      child.style.cssText = prev;

      const needsMore = count < tabs.length - 1;
      if (usedWidth + w + (needsMore ? moreButtonWidth : 0) <= availableWidth) {
        usedWidth += w;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count < tabs.length ? Math.max(count, 1) : tabs.length);
  }, [tabs.length]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => measureTabs());
    ro.observe(nav);
    const raf = requestAnimationFrame(() => measureTabs());
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [measureTabs]);

  const resolvedVisibleCount = visibleCount ?? 0;
  const overflowTabs = resolvedVisibleCount < tabs.length ? tabs.slice(resolvedVisibleCount) : [];
  const activeInOverflow = overflowTabs.some((t) => isTabActive(t.path));

  return (
    <nav className="flex items-center border-b border-border">
      <div ref={navRef} className="relative flex items-center flex-1 min-w-0 overflow-hidden">
        {tabs.map((tab, idx) => {
          const isActive = isTabActive(tab.path);
          const isVisible = visibleCount !== null && idx < visibleCount;
          const isMeasuring = visibleCount === null;
          const hiddenClass = isMeasuring || !isVisible ? "absolute invisible pointer-events-none" : "";

          return (
            <button
              key={tab.path}
              data-tab
              onClick={() => onNavigate(tab.path)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px whitespace-nowrap shrink-0 ${
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              } ${hiddenClass}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}

        {overflowTabs.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 ${
                  activeInOverflow
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
                <span>{t("publicLayout.more")}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {overflowTabs.map((tab) => {
                const isActive = isTabActive(tab.path);
                return (
                  <DropdownMenuItem
                    key={tab.path}
                    onClick={() => onNavigate(tab.path)}
                    className={isActive ? "bg-accent text-accent-foreground font-medium" : ""}
                  >
                    <tab.icon className="w-3.5 h-3.5 mr-2" />
                    {tab.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {rightSlot && <div className="ml-auto">{rightSlot}</div>}
    </nav>
  );
}

export function PublicChampionshipLayout({ children }: {children: ReactNode;}) {
  const { slug } = useParams<{slug: string;}>();

  return (
    <PublicChampionshipProvider slug={slug || ""}>
      <InnerLayout>{children}</InnerLayout>
    </PublicChampionshipProvider>);

}